import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  Bot,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  DollarSign,
  Filter,
  MessageCircle,
  MoreHorizontal,
  Phone,
  RefreshCw,
  Search,
  Send,
  Tag,
  User,
  Users,
  XCircle,
} from 'lucide-react';

import { useTheme } from '../contexts/ThemeContext.tsx';

type LeadMode = 'ia' | 'humano' | 'misto';
type LeadPriority = 'critical' | 'warning' | 'normal' | 'success';

type LeadPreview = {
  id: number;
  name: string;
  phone: string;
  stage: string;
  nextAction: string;
  nextActionAt: string;
  lastMessage: string;
  appointment?: string;
  source: string;
  owner: string;
  mode: LeadMode;
  priority: LeadPriority;
  tags: string[];
  followUp: string;
  value?: string;
  unread: number;
};

type WorkView = {
  id: string;
  label: string;
  count: number;
  icon: React.ElementType;
};

const leads: LeadPreview[] = [
  {
    id: 1,
    name: 'Maria Fernanda',
    phone: '55 21 99999-1032',
    stage: 'Agendado',
    nextAction: 'Confirmar consulta',
    nextActionAt: 'Hoje 16:00',
    lastMessage: 'Pode confirmar para mim, por favor?',
    appointment: 'Amanha 14:00',
    source: 'Meta Ads',
    owner: 'Bruna',
    mode: 'ia',
    priority: 'critical',
    tags: ['Implante', 'Quente'],
    followUp: 'Confirmacao pendente',
    unread: 2,
  },
  {
    id: 2,
    name: 'Joao Pedro',
    phone: '55 11 98888-4501',
    stage: 'Sem resposta',
    nextAction: 'Follow-up vencido',
    nextActionAt: 'Venceu ha 2h',
    lastMessage: 'Vou ver com minha esposa e retorno.',
    source: 'Instagram',
    owner: 'Elisa',
    mode: 'humano',
    priority: 'warning',
    tags: ['Orcamento', 'Aparelho'],
    followUp: 'Passo 3 de 5',
    unread: 0,
  },
  {
    id: 3,
    name: 'Ana Clara',
    phone: '55 31 97777-9930',
    stage: 'Compareceu',
    nextAction: 'Registrar venda',
    nextActionAt: 'Hoje',
    lastMessage: 'Gostei do plano, quero fechar.',
    appointment: 'Ontem 09:30',
    source: 'Indicacao',
    owner: 'Matheus',
    mode: 'misto',
    priority: 'success',
    tags: ['Lente', 'Fechamento'],
    followUp: 'Pos-consulta ativo',
    value: 'R$ 4.800',
    unread: 1,
  },
  {
    id: 4,
    name: 'Carlos Henrique',
    phone: '55 21 96666-2044',
    stage: 'No-show',
    nextAction: 'Recuperar falta',
    nextActionAt: 'Amanha 09:00',
    lastMessage: 'Tive um imprevisto no trabalho.',
    appointment: 'Seg 10:00',
    source: 'Google',
    owner: 'Sofia',
    mode: 'ia',
    priority: 'warning',
    tags: ['Avaliacao', 'No-show'],
    followUp: 'No-show passo 1',
    unread: 0,
  },
  {
    id: 5,
    name: 'Patricia Lima',
    phone: '55 41 95555-7182',
    stage: 'Novo lead',
    nextAction: 'Qualificar interesse',
    nextActionAt: 'Agora',
    lastMessage: 'Quero saber valores de clareamento.',
    source: 'WhatsApp',
    owner: 'IA',
    mode: 'ia',
    priority: 'normal',
    tags: ['Clareamento'],
    followUp: 'Primeiro atendimento',
    unread: 3,
  },
];

const workViews: WorkView[] = [
  { id: 'today', label: 'Hoje', count: 18, icon: Clock },
  { id: 'late', label: 'Atrasados', count: 7, icon: AlertTriangle },
  { id: 'waiting', label: 'Sem resposta', count: 24, icon: MessageCircle },
  { id: 'scheduled', label: 'Agendados', count: 31, icon: Calendar },
  { id: 'confirm', label: 'Confirmar', count: 12, icon: Send },
  { id: 'noshow', label: 'No-show', count: 9, icon: XCircle },
  { id: 'sales', label: 'Orcamentos', count: 14, icon: DollarSign },
];

const timeline = [
  { title: 'Lead entrou no sistema', meta: 'Meta Ads, ha 3 dias', icon: Users },
  { title: 'IA iniciou atendimento', meta: 'Perguntou interesse e disponibilidade', icon: Bot },
  { title: 'Consulta agendada', meta: 'Amanha 14:00, criada pela IA', icon: Calendar },
  { title: 'Confirmacao pendente', meta: 'Disparo programado para hoje 16:00', icon: Send },
];

const priorityClasses: Record<LeadPriority, string> = {
  critical: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:ring-rose-900',
  warning: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900',
  normal: 'bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-900',
  success: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900',
};

const modeConfig: Record<LeadMode, { label: string; icon: React.ElementType; className: string }> = {
  ia: {
    label: 'IA',
    icon: Bot,
    className: 'bg-sminy-primary/10 text-sminy-primary ring-sminy-primary/25',
  },
  humano: {
    label: 'Humano',
    icon: User,
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900',
  },
  misto: {
    label: 'Misto',
    icon: Users,
    className: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300 dark:ring-violet-900',
  },
};

const CRMListPreview: React.FC = () => {
  const { isDark } = useTheme();
  const [activeView, setActiveView] = useState('today');
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(leads[0].id);

  const filteredLeads = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return leads;
    return leads.filter((lead) => {
      return [
        lead.name,
        lead.phone,
        lead.stage,
        lead.source,
        lead.owner,
        ...lead.tags,
      ].some((value) => value.toLowerCase().includes(normalized));
    });
  }, [query]);

  const selectedLead = filteredLeads.find((lead) => lead.id === selectedId) || filteredLeads[0] || null;

  return (
    <div className={isDark ? 'min-h-screen bg-[#111827] text-gray-200' : 'min-h-screen bg-gray-50 text-gray-900'}>
      <div className="mx-auto flex w-full max-w-[1680px] flex-col gap-4 px-4 py-4 sm:px-6">
        <header className="flex flex-col gap-3 border-b border-gray-200 pb-4 dark:border-gray-700 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-xl font-semibold">CRM operacional</h1>
            <p className={isDark ? 'mt-1 text-sm text-gray-400' : 'mt-1 text-sm text-gray-600'}>
              Lista priorizada para atendimento, follow-ups, confirmacoes e recuperacao.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative min-w-0 sm:w-80">
              <Search className={isDark ? 'pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-500' : 'pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400'} />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Buscar lead, telefone, origem ou tag"
                className={isDark
                  ? 'h-9 w-full rounded-lg border border-gray-700 bg-gray-800 pl-9 pr-3 text-sm text-gray-200 outline-none focus:border-sminy-primary'
                  : 'h-9 w-full rounded-lg border border-gray-200 bg-white pl-9 pr-3 text-sm text-gray-900 outline-none focus:border-sminy-primary'}
              />
            </div>

            <button
              type="button"
              className={isDark
                ? 'inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-3 text-sm font-medium text-gray-200 hover:bg-gray-700'
                : 'inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-100'}
            >
              <Filter className="h-4 w-4" />
              Filtros
            </button>
          </div>
        </header>

        <main className="grid min-h-[calc(100vh-140px)] grid-cols-1 gap-4 xl:grid-cols-[220px_minmax(0,1fr)_420px]">
          <aside className={isDark ? 'rounded-lg border border-gray-700 bg-gray-800 p-3' : 'rounded-lg border border-gray-200 bg-white p-3'}>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm font-semibold">Visoes</span>
              <button
                type="button"
                className={isDark ? 'rounded-md p-1 text-gray-400 hover:bg-gray-700' : 'rounded-md p-1 text-gray-500 hover:bg-gray-100'}
                aria-label="Mais opcoes"
              >
                <MoreHorizontal className="h-4 w-4" />
              </button>
            </div>

            <nav className="flex gap-2 overflow-x-auto pb-1 xl:flex-col xl:overflow-visible xl:pb-0">
              {workViews.map((view) => {
                const Icon = view.icon;
                const active = activeView === view.id;
                return (
                  <button
                    key={view.id}
                    type="button"
                    onClick={() => setActiveView(view.id)}
                    className={[
                      'flex min-w-fit items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition',
                      active
                        ? 'bg-sminy-primary/10 text-sminy-primary ring-1 ring-sminy-primary/25'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-100',
                    ].join(' ')}
                  >
                    <span className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {view.label}
                    </span>
                    <span className={active ? 'text-sminy-primary' : isDark ? 'text-gray-500' : 'text-gray-400'}>
                      {view.count}
                    </span>
                  </button>
                );
              })}
            </nav>

            <div className={isDark ? 'mt-4 rounded-lg bg-gray-900 p-3' : 'mt-4 rounded-lg bg-gray-50 p-3'}>
              <div className="flex items-center gap-2 text-sm font-semibold">
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                SLA da fila
              </div>
              <div className="mt-3 space-y-2 text-xs">
                <QueueMetric label="Criticos" value="5" tone="rose" isDark={isDark} />
                <QueueMetric label="Vencem hoje" value="18" tone="amber" isDark={isDark} />
                <QueueMetric label="Com IA" value="42" tone="sky" isDark={isDark} />
              </div>
            </div>
          </aside>

          <section className={isDark ? 'overflow-hidden rounded-lg border border-gray-700 bg-gray-800' : 'overflow-hidden rounded-lg border border-gray-200 bg-white'}>
            <div className={isDark ? 'flex flex-col gap-3 border-b border-gray-700 px-4 py-3 lg:flex-row lg:items-center lg:justify-between' : 'flex flex-col gap-3 border-b border-gray-200 px-4 py-3 lg:flex-row lg:items-center lg:justify-between'}>
              <div>
                <h2 className="text-sm font-semibold">Fila de trabalho</h2>
                <p className={isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>
                  {filteredLeads.length} contatos na visao selecionada
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <StagePill label="Todos" active />
                <StagePill label="IA" />
                <StagePill label="Humano" />
                <StagePill label="Vencidos" />
              </div>
            </div>

            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {filteredLeads.length > 0 ? (
                filteredLeads.map((lead) => (
                  <LeadRow
                    key={lead.id}
                    lead={lead}
                    selected={selectedLead?.id === lead.id}
                    isDark={isDark}
                    onSelect={() => setSelectedId(lead.id)}
                  />
                ))
              ) : (
                <div className="flex min-h-64 flex-col items-center justify-center px-6 py-10 text-center">
                  <Search className={isDark ? 'h-8 w-8 text-gray-600' : 'h-8 w-8 text-gray-300'} />
                  <p className="mt-3 text-sm font-semibold">Nenhum contato encontrado</p>
                  <p className={isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>
                    Ajuste a busca ou os filtros da fila.
                  </p>
                </div>
              )}
            </div>
          </section>

          {selectedLead ? <LeadDetailPanel lead={selectedLead} isDark={isDark} /> : <EmptyDetailPanel isDark={isDark} />}
        </main>
      </div>
    </div>
  );
};

const QueueMetric: React.FC<{ label: string; value: string; tone: 'rose' | 'amber' | 'sky'; isDark: boolean }> = ({
  label,
  value,
  tone,
  isDark,
}) => {
  const toneClass = {
    rose: 'bg-rose-500',
    amber: 'bg-amber-500',
    sky: 'bg-sminy-primary',
  }[tone];

  return (
    <div className="flex items-center justify-between">
      <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>{label}</span>
      <span className="flex items-center gap-2 font-semibold">
        <span className={`h-2 w-2 rounded-full ${toneClass}`} />
        {value}
      </span>
    </div>
  );
};

const StagePill: React.FC<{ label: string; active?: boolean }> = ({ label, active }) => (
  <button
    type="button"
    className={active
      ? 'rounded-lg bg-sminy-primary px-3 py-1.5 text-xs font-semibold text-white'
      : 'rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'}
  >
    {label}
  </button>
);

const LeadRow: React.FC<{
  lead: LeadPreview;
  selected: boolean;
  isDark: boolean;
  onSelect: () => void;
}> = ({ lead, selected, isDark, onSelect }) => {
  const mode = modeConfig[lead.mode];
  const ModeIcon = mode.icon;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'grid w-full grid-cols-1 gap-3 px-4 py-4 text-left transition lg:grid-cols-[minmax(220px,1.2fr)_minmax(220px,1fr)_minmax(180px,.8fr)_32px]',
        selected
          ? 'bg-sminy-primary/10 dark:bg-sminy-primary/10'
          : isDark
            ? 'hover:bg-gray-700/60'
            : 'hover:bg-gray-50',
      ].join(' ')}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-semibold">{lead.name}</span>
          {lead.unread > 0 && (
            <span className="rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {lead.unread}
            </span>
          )}
        </div>
        <div className={isDark ? 'mt-1 flex items-center gap-2 text-xs text-gray-400' : 'mt-1 flex items-center gap-2 text-xs text-gray-500'}>
          <Phone className="h-3.5 w-3.5" />
          <span>{lead.phone}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {lead.tags.map((tag) => (
            <span
              key={tag}
              className={isDark ? 'rounded-md bg-gray-700 px-2 py-1 text-[11px] text-gray-300' : 'rounded-md bg-gray-100 px-2 py-1 text-[11px] text-gray-600'}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>

      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`rounded-md px-2 py-1 text-xs font-semibold ring-1 ${priorityClasses[lead.priority]}`}>
            {lead.stage}
          </span>
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ${mode.className}`}>
            <ModeIcon className="h-3.5 w-3.5" />
            {mode.label}
          </span>
        </div>
        <p className={isDark ? 'mt-2 truncate text-sm text-gray-300' : 'mt-2 truncate text-sm text-gray-700'}>
          {lead.lastMessage}
        </p>
      </div>

      <div className="min-w-0">
        <p className="truncate text-sm font-semibold">{lead.nextAction}</p>
        <p className={lead.priority === 'critical' ? 'mt-1 text-xs font-semibold text-rose-600 dark:text-rose-300' : isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>
          {lead.nextActionAt}
        </p>
        <p className={isDark ? 'mt-1 truncate text-xs text-gray-500' : 'mt-1 truncate text-xs text-gray-400'}>
          {lead.followUp}
        </p>
      </div>

      <div className="hidden items-center justify-end lg:flex">
        <ChevronRight className={selected ? 'h-4 w-4 text-sminy-primary' : isDark ? 'h-4 w-4 text-gray-500' : 'h-4 w-4 text-gray-400'} />
      </div>
    </button>
  );
};

const EmptyDetailPanel: React.FC<{ isDark: boolean }> = ({ isDark }) => (
  <aside className={isDark ? 'rounded-lg border border-gray-700 bg-gray-800 p-6' : 'rounded-lg border border-gray-200 bg-white p-6'}>
    <div className="flex min-h-96 flex-col items-center justify-center text-center">
      <Users className={isDark ? 'h-9 w-9 text-gray-600' : 'h-9 w-9 text-gray-300'} />
      <h2 className="mt-3 text-sm font-semibold">Sem lead selecionado</h2>
      <p className={isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>
        Selecione um contato da fila para abrir o resumo operacional.
      </p>
    </div>
  </aside>
);

const LeadDetailPanel: React.FC<{ lead: LeadPreview; isDark: boolean }> = ({ lead, isDark }) => {
  const mode = modeConfig[lead.mode];
  const ModeIcon = mode.icon;

  return (
    <aside className={isDark ? 'rounded-lg border border-gray-700 bg-gray-800' : 'rounded-lg border border-gray-200 bg-white'}>
      <div className={isDark ? 'border-b border-gray-700 p-4' : 'border-b border-gray-200 p-4'}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-sminy-primary text-sm font-bold text-white">
                {lead.name.split(' ').slice(0, 2).map((word) => word[0]).join('')}
              </div>
              <div className="min-w-0">
                <h2 className="truncate text-base font-semibold">{lead.name}</h2>
                <p className={isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>{lead.phone}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className={isDark ? 'rounded-md p-1.5 text-gray-400 hover:bg-gray-700' : 'rounded-md p-1.5 text-gray-500 hover:bg-gray-100'}
            aria-label="Mais opcoes"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <InfoTile label="Origem" value={lead.source} isDark={isDark} />
          <InfoTile label="Responsavel" value={lead.owner} isDark={isDark} />
          <InfoTile label="Consulta" value={lead.appointment || 'Nao marcada'} isDark={isDark} />
          <InfoTile label="Valor" value={lead.value || 'Em aberto'} isDark={isDark} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <span className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-semibold ring-1 ${mode.className}`}>
            <ModeIcon className="h-3.5 w-3.5" />
            {mode.label}
          </span>
          <span className={`rounded-md px-2 py-1 text-xs font-semibold ring-1 ${priorityClasses[lead.priority]}`}>
            {lead.nextAction}
          </span>
        </div>
      </div>

      <div className={isDark ? 'border-b border-gray-700 p-4' : 'border-b border-gray-200 p-4'}>
        <div className="grid grid-cols-2 gap-2">
          <ActionButton icon={Calendar} label="Agendar" isDark={isDark} />
          <ActionButton icon={RefreshCw} label="Reagendar" isDark={isDark} />
          <ActionButton icon={CheckCircle2} label="Compareceu" isDark={isDark} />
          <ActionButton icon={XCircle} label="No-show" isDark={isDark} />
          <ActionButton icon={DollarSign} label="Venda" isDark={isDark} />
          <ActionButton icon={MessageCircle} label="Chat" isDark={isDark} />
        </div>
      </div>

      <div className={isDark ? 'border-b border-gray-700 p-4' : 'border-b border-gray-200 p-4'}>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Proxima acao</h3>
          <span className={isDark ? 'text-xs text-gray-400' : 'text-xs text-gray-500'}>{lead.nextActionAt}</span>
        </div>
        <div className={isDark ? 'rounded-lg bg-gray-900 p-3' : 'rounded-lg bg-gray-50 p-3'}>
          <p className="text-sm font-semibold">{lead.nextAction}</p>
          <p className={isDark ? 'mt-1 text-xs text-gray-400' : 'mt-1 text-xs text-gray-500'}>{lead.followUp}</p>
        </div>
      </div>

      <div className="p-4">
        <h3 className="mb-3 text-sm font-semibold">Timeline</h3>
        <div className="space-y-3">
          {timeline.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className="flex gap-3">
                <div className={isDark ? 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-700 text-gray-300' : 'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-600'}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium">{item.title}</p>
                  <p className={isDark ? 'mt-0.5 text-xs text-gray-400' : 'mt-0.5 text-xs text-gray-500'}>{item.meta}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {lead.tags.map((tag) => (
            <span
              key={tag}
              className={isDark ? 'inline-flex items-center gap-1 rounded-md bg-gray-700 px-2 py-1 text-xs text-gray-300' : 'inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-600'}
            >
              <Tag className="h-3 w-3" />
              {tag}
            </span>
          ))}
        </div>
      </div>
    </aside>
  );
};

const InfoTile: React.FC<{ label: string; value: string; isDark: boolean }> = ({ label, value, isDark }) => (
  <div className={isDark ? 'rounded-lg bg-gray-900 p-3' : 'rounded-lg bg-gray-50 p-3'}>
    <p className={isDark ? 'text-[11px] font-medium text-gray-400' : 'text-[11px] font-medium text-gray-500'}>{label}</p>
    <p className="mt-1 truncate text-sm font-semibold">{value}</p>
  </div>
);

const ActionButton: React.FC<{ icon: React.ElementType; label: string; isDark: boolean }> = ({
  icon: Icon,
  label,
  isDark,
}) => (
  <button
    type="button"
    className={isDark
      ? 'inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-700 bg-gray-900 px-2 text-xs font-semibold text-gray-200 hover:border-sminy-primary hover:text-sminy-primary'
      : 'inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-2 text-xs font-semibold text-gray-700 hover:border-sminy-primary hover:text-sminy-primary'}
  >
    <Icon className="h-4 w-4" />
    {label}
  </button>
);

export default CRMListPreview;
